import React, { useState, useRef } from 'react';
import { CameraIcon, UserCircleIcon } from '@heroicons/react/24/outline';

interface AvatarProps {
  src?: string;
  name: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  editable?: boolean;
  onImageChange?: (file: File) => void;
  className?: string;
}

const getBackendUrl = (path: string) => {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};

const Avatar: React.FC<AvatarProps> = ({
  src,
  name,
  size = 'md',
  editable = false,
  onImageChange,
  className = '',
}) => {
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16',
    xl: 'h-24 w-24',
  };

  const handleImageClick = () => {
    if (editable && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (editable && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      handleImageClick();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onImageChange) {
      onImageChange(file);
    }
  };

  const shouldShowImage = Boolean(src) && !imageError;
  const imageUrl =
    src && typeof src === 'string' && src.startsWith('/uploads/') ? getBackendUrl(src) : src;

  return (
    <div className={`relative inline-block ${className}`}>
      <div
        className={`
          ${sizeClasses[size]}
          rounded-full
          flex items-center justify-center
          ${shouldShowImage ? 'bg-transparent' : 'bg-slate-100 text-slate-400'}
          ${editable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
          overflow-hidden
        `}
        onClick={handleImageClick}
        onKeyDown={handleKeyDown}
        role={editable ? 'button' : undefined}
        tabIndex={editable ? 0 : undefined}
        aria-label={editable ? `Alterar foto de ${name}` : name ? `Foto de ${name}` : 'Sem foto'}
      >
        {shouldShowImage ? (
          <img
            src={imageUrl}
            alt={name || 'Foto do usuário'}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <UserCircleIcon className="w-2/3 h-2/3" aria-hidden />
        )}
      </div>

      {editable && (
        <>
          <div
            className="absolute inset-0 rounded-full bg-black bg-opacity-0 hover:bg-opacity-30 flex items-center justify-center transition-all duration-200 cursor-pointer"
            onClick={handleImageClick}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
            aria-label={`Alterar foto de ${name}`}
          >
            <CameraIcon className="h-6 w-6 text-white opacity-0 hover:opacity-100 transition-opacity" />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </>
      )}
    </div>
  );
};

export default Avatar;
